import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { cast, types } from 'mobx-state-tree';
import { UNIT_TYPES } from 'src/constants/unit';
const InUnitAmenity = types.model({
  inUnitAmenityId: types.string,
  inUnitAmenityOptionId: types.string
});
const UnitUtility = types.model({
  utilityId: types.string,
  allocationPercentage: types.maybeNull(types.union(types.number, types.string))
});
const AmenitiesUnit = types.model({
  id: types.string,
  name: types.string,
  optionId: types.string,
  optionName: types.string
});
const UnitSetupItem = types.model({
  disabledOptOutUtilityIds: types.array(types.string),
  id: types.string,
  name: types.string,
  occupancy: types.string,
  square: types.string,
  type: types.string,
  // The id this unit carries in the property management software. Empty means unmapped, which
  // the charge export leaves as an empty cell rather than guessing.
  pmsUnitId: types.optional(types.string, ''),
  // The name the PMS knows the unit by. Optional, and no substitute for the id: the import
  // matches on the id, so a unit with only a name still counts as unmapped.
  pmsUnitName: types.optional(types.string, ''),
  amenities: types.array(InUnitAmenity),
  unitUtilities: types.array(UnitUtility)
});
const UnitSetupItemValidation = types.model({
  name: types.boolean,
  occupancy: types.boolean,
  square: types.boolean
});
export const UnitSetup = types.model({
  amenities: types.optional(types.array(AmenitiesUnit), []),
  units: types.optional(types.array(UnitSetupItem), [{
    disabledOptOutUtilityIds: [],
    id: 'null',
    name: '1',
    occupancy: '1',
    square: '0',
    type: 'None',
    pmsUnitId: '',
    pmsUnitName: '',
    amenities: [],
    unitUtilities: []
  }]),
  unitsValidation: types.optional(types.array(UnitSetupItemValidation), [{
    name: true,
    occupancy: true,
    square: true
  }])
}).views(store => ({
  /** How many units carry a PMS id, for the "12 of 30 mapped" line above the list. */
  get mappedUnitsCount() {
    return store.units.filter(unit => unit.pmsUnitId.trim().length > 0).length;
  },
  getOptOutUtilityIds(index, id) {
    const findIdx = store.units[index].disabledOptOutUtilityIds.findIndex(item => item === id);
    if (findIdx !== -1) {
      return store.units[index].disabledOptOutUtilityIds[findIdx];
    }
    return false;
  }
})).actions(store => ({
  validateValue(index, id, value) {
    try {
      store.unitsValidation[index][id] = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeName(index, data) {
    try {
      store.units[index].name = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changePmsUnitName(index, data) {
    try {
      store.units[index].pmsUnitName = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changePmsUnitId(index, data) {
    try {
      store.units[index].pmsUnitId = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  /**
   * Applies a whole set of PMS mappings at once — what the CSV import hands back. Keyed by
   * position in the list rather than by unit id, because units added in this session have no id
   * yet. A position the map does not name keeps the mapping it already has.
   */
  applyPmsMappings(byIndex) {
    try {
      Object.entries(byIndex).forEach(_ref => {
        var _mapping$id, _mapping$name;
        let _ref2 = _slicedToArray(_ref, 2),
          index = _ref2[0],
          mapping = _ref2[1];
        const unit = store.units[Number(index)];
        if (!unit) return;
        unit.pmsUnitId = (_mapping$id = mapping === null || mapping === void 0 ? void 0 : mapping.id) !== null && _mapping$id !== void 0 ? _mapping$id : '';
        unit.pmsUnitName = (_mapping$name = mapping === null || mapping === void 0 ? void 0 : mapping.name) !== null && _mapping$name !== void 0 ? _mapping$name : '';
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeOccupancy(index, data) {
    try {
      store.units[index].occupancy = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeUnitUtilities(index, utilityId, value) {
    try {
      var _store$units$index;
      const utility = (_store$units$index = store.units[index]) === null || _store$units$index === void 0 ? void 0 : _store$units$index.unitUtilities.find(u => u.utilityId === utilityId);
      if (!utility) {
        store.units[index].unitUtilities.push({
          utilityId,
          allocationPercentage: value
        });
        return;
      }
      utility.allocationPercentage = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeSquare(index, data) {
    try {
      store.units[index].square = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeType(index, data) {
    try {
      store.units[index].type = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeAmenities(index, data) {
    try {
      store.units[index].amenities = cast([]);
      data.map(dataItem => {
        const amenity = store.amenities.find(item => item.optionId === dataItem);
        if (amenity) {
          store.units[index].amenities.push({
            inUnitAmenityId: amenity.id,
            inUnitAmenityOptionId: amenity.optionId
          });
        }
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setAmenities(data) {
    try {
      store.amenities = cast([]);
      data.map(amenityItem => {
        amenityItem.inUnitAmenityOptions.map(optionItem => {
          store.amenities.push({
            id: amenityItem.id,
            name: amenityItem.name,
            optionId: optionItem.id,
            optionName: optionItem.name
          });
        });
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeOptOutUtilityIds(index, data) {
    try {
      if (store.units[index].disabledOptOutUtilityIds.includes(data)) {
        const currentOptOutUtilityIds = store.units[index].disabledOptOutUtilityIds.filter(item => item !== data);
        store.units[index].disabledOptOutUtilityIds = cast(currentOptOutUtilityIds);
      } else {
        store.units[index].disabledOptOutUtilityIds.push(data);
      }
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeSavedUnits(resp) {
    let defaultOccupancy = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '1';
    const data = [...resp.response].sort((a, b) => {
      var _ref3;
      return (_ref3 = '' + (a === null || a === void 0 ? void 0 : a.name)) === null || _ref3 === void 0 ? void 0 : _ref3.localeCompare(b === null || b === void 0 ? void 0 : b.name, undefined, {
        numeric: true
      });
    });
    try {
      store.units = cast([]);
      store.unitsValidation = cast([]);
      if (data.length) {
        data.map(item => {
          var _item$pmsUnitId, _item$pmsUnitName, _item$pmsUnitName2;
          const unitType = UNIT_TYPES.find(type => type.key === item.type);
          store.units.push({
            disabledOptOutUtilityIds: item.utilityIds,
            id: item.id,
            name: item.name,
            occupancy: "".concat(item.occupancy),
            square: "".concat(item.squareFootage),
            type: unitType ? unitType.key : 'None',
            pmsUnitId: (_item$pmsUnitId = item.pmsUnitId) !== null && _item$pmsUnitId !== void 0 ? _item$pmsUnitId : '',
            pmsUnitName: (_item$pmsUnitName = item.pmsUnitName) !== null && _item$pmsUnitName !== void 0 ? _item$pmsUnitName : '',
            pmsUnitName: (_item$pmsUnitName2 = item.pmsUnitName) !== null && _item$pmsUnitName2 !== void 0 ? _item$pmsUnitName2 : '',
            amenities: item.inUnitAmenities,
            unitUtilities: item.unitUtilities
          });
          store.unitsValidation.push({
            name: true,
            occupancy: true,
            square: true
          });
        });
      } else {
        store.units.push({
          disabledOptOutUtilityIds: [],
          id: 'null',
          name: '1',
          occupancy: defaultOccupancy,
          square: '0',
          type: 'None',
          pmsUnitId: '',
          pmsUnitName: '',
          pmsUnitName: '',
          amenities: [],
          unitUtilities: []
        });
        store.unitsValidation.push({
          name: true,
          occupancy: true,
          square: true
        });
      }
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  initIdsAfterAddingUnits(data) {
    try {
      data.map((item, idx) => {
        store.units[idx].id = item;
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  chageCount(data) {
    let defaultOccupancy = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '1';
    try {
      if (store.units.length < data) {
        for (let index = store.units.length; index < data; index++) {
          store.units.push({
            id: 'null',
            name: "".concat(index + 1),
            occupancy: defaultOccupancy,
            square: '0',
            type: 'None',
            pmsUnitId: '',
            pmsUnitName: '',
            pmsUnitName: '',
            pmsUnitName: '',
            amenities: [],
            disabledOptOutUtilityIds: [],
            unitUtilities: []
          });
          store.unitsValidation.push({
            name: true,
            occupancy: true,
            square: true
          });
        }
        return;
      }
      store.units.splice(data, store.units.length - data);
      store.unitsValidation.splice(data, store.unitsValidation.length - data);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  saveDisabledOptOutUtilityIds(index, ids) {
    try {
      store.units[index].disabledOptOutUtilityIds = cast(ids);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
}));